import * as idb from '@/lib/idbd';
import Auth from '@/Auth/Auth';
import ENTITIES from '@/store/actions/index';
import store from '@/store/store';
import * as StatusBarActions from "@/store/actions/statusBarActions";
import * as MiscActions from "@/store/actions/miscActions";
import pWaitFor from 'p-wait-for';
import locals from '@/lib/localStorage';
import ls from '@/lib/localStorage';
import {stopTimer} from "@/TaskManagement/timers";
import {distinctFilter} from "@/lib/picos/filters";
import {ensure} from "@/lib/picos/ensure";
import {isConnected} from "@/lib/picos/browserFns";
import {findMany, findManyParallel, remove, restore, save, saveMany} from "@/rest/api";
import {isPortalUser} from "@/lib/picos/currentLogin";
import {RestoreStatus} from "@/store/actions/miscActions";
import {socket} from "@/lib/picos/socket";
import Document from "@/entities/document";
import Task from "@/entities/task";
import {globalAbortSignal} from "@/lib/picos/abortController";
import CaseHelper from "@/Case/CaseHelper";

/**
 * Set to true to print out copious logging.
 * @type {boolean}
 */
let logging: boolean = ls.getBoolean(ls.QUEUE_LOGGING, false);

export type ProcessQueueMethod = 'full' | 'partial';

interface ProcessQueueOptions {
    type?: ProcessQueueMethod;
}

// `okDatabaseCodes` constant removed (Tier 5.1 — 2026-05-08): the bounded-
// retry rewrite made it dead in this file. The same constant still lives
// (identically) in every saga reducer (e.g. taskReducer.ts:370) where it
// gates the per-entity `fatalError.recoverable` flag consumed by the Cache
// Management UI at Support/Pages/CacheManagement.tsx:86. The processQueue
// itself no longer distinguishes — every fatalError gets bounded retry
// regardless of whether it originated from a "recoverable" DB code.

export async function waitForProcessingToFinish(timeoutInMillis: number, intervalInMillis: number) {
    // We do not allow mixing uploads and downloads, as it leads to items "disappearing" until the next download.
    // Instead, we wait for uploading/fetching to stop before moving forward.
    try {
        if (store.getState().statusBar.isFetching || store.getState().statusBar.isUploading) {
            if (logging) console.debug('%c⮃ processQueue: waiting for fetching and uploading to finish', 'color: orange');

            if (logging) console.debug(`⮃ processQueue: Waiting 1.5 seconds for processing to finish before processing the queue.`);
            await pWaitFor(() => {
                return !store.getState().statusBar.isFetching && !store.getState().statusBar.isUploading;
            }, {timeout: timeoutInMillis, interval: intervalInMillis, signal: globalAbortSignal});

            if (logging) console.debug('%c⮃ processQueue: fetching and uploading finished', 'color: green');
        }
    }
    catch (err) {
        // We waited 3 minutes, probably time to move on
        if (logging) console.warn(`%c⮃ processQueue: processQueue waiting ${timeoutInMillis/1000} seconds for fetching/uploading to end, moving on`, {color: 'orange'});
    }

}

// Mutex to prevent concurrent processQueue executions from racing on IDB reads
let _processQueueRunning = false;

// Test-only bypass for the connection/auth/socket readiness gates in
// _runProcessQueue. Used by Playwright queue specs that want to exercise
// the lock/retry/healing logic without depending on socket.io being
// connected in the test environment. Toggled via __test_retry.setBypassReadiness.
// PROD-stripped because the entire __test_retry surface is import.meta.env.PROD-gated.
let _testBypassReadinessGates = false;

// Bounded retry tracker for items with `fatalError`. Keyed by
// `${entityName}:${id}:${addedOn}`. The reducer rewrites fatalError fresh on
// every saveFailure so we can't store attempt counts there; we track them
// here instead. Stale entries self-evict after MAX_FATAL_ATTEMPTS.
//
// Why retry essentially everything: the per-status classification used to
// strand items on the first non-{500-with-DB-code,502,503} failure, which
// trapped users behind real bugs:
//   - 401: token expired between polls — re-auth would have succeeded
//   - 408/429: timeout / rate limit — succeed once load clears
//   - 409: concurrent-update conflict — succeed once parent state syncs
//   - 504: gateway timeout — succeed when upstream returns
//   - Network/fetch throws (status 0): blip — succeed instantly on retry
// Empirically, almost every "stuck" item resolved when users hit the Retry
// Cache button (which clears fatalError on every queued item via idb.resetAll).
// This bounded retry replicates that auto-recovery without manual intervention,
// while capping wasted requests on items that are genuinely broken.
type RetryState = {attempts: number; nextRetryAt: number};
const recoverableRetryState = new Map<string, RetryState>();

// Cap on retries before an item is considered STUCK and surfaces in the
// Cache Management UI for human review.
const MAX_FATAL_ATTEMPTS = 10;

// Per-call wall-clock ceiling for save/saveMany/restore/remove/findMany. Five
// minutes accommodates server-side connectWithRetry budgets + slow batches +
// payload assembly with healthy headroom; an actually-hung request recovers
// within this window. A timeout is treated as a real failure (the item retries
// via the existing bounded-retry path with backoff) — there's no special-case
// handling because server-side `addedOn` idempotency makes a duplicate POST
// (from a re-enqueued item that the server already received) benign.
const REQUEST_TIMEOUT_MS = 5 * 60 * 1000;

// Items older than this are considered "stalled" and trigger the StatusBar
// maroon indicator. Items in active healthy backoff (nextRetryAt > now AND
// attempts < MAX_FATAL_ATTEMPTS) are excluded — they're being retried, not
// stuck.
const QUEUE_STALL_THRESHOLD_MS = 5 * 60 * 1000;

/**
 * Wrap a network call with a 5-min AbortController + globalAbortSignal chain.
 * The signal aborts on logout (chains globalAbortSignal) or after 5 minutes.
 *
 * The {once: true} + removeEventListener pair prevents listener accumulation
 * on globalAbortSignal, which is single-fire and persistent for the page
 * lifetime.
 */
async function withTimeout<T>(label: string, fn: (signal: AbortSignal) => Promise<T>): Promise<T> {
    const ac = new AbortController();
    const onGlobalAbort = () => ac.abort('logout');
    globalAbortSignal.addEventListener('abort', onGlobalAbort, {once: true});
    const timer = setTimeout(() => {
        ac.abort('timeout');
        console.warn(`⮃ processQueue: ${label} aborted after ${REQUEST_TIMEOUT_MS / 1000}s timeout`);
    }, REQUEST_TIMEOUT_MS);
    try {
        return await fn(ac.signal);
    } finally {
        clearTimeout(timer);
        globalAbortSignal.removeEventListener('abort', onGlobalAbort);
    }
}

// Cross-tab retry-state sync. Multi-tab is the norm in this app (users
// commonly keep case + invoice + task + calendar tabs open at once), so
// per-tab counters would let the same item burn ~MAX_FATAL_ATTEMPTS×tabCount
// retries before STUCK as the Web Lock alternates between tabs.
//
// BroadcastChannel propagates each retry decision to every other tab in
// real-time. The Web Lock at the top of processQueue() guarantees only one
// tab makes a decision at a time; the broadcast just synchronizes the
// resulting bookkeeping across the rest. Ephemeral by design — tab close
// drops state, page reload starts fresh for the new tab (matching the
// existing in-memory semantics).
//
// BroadcastChannel support: Chrome 54+, Firefox 38+, Safari 15.4+. Falls
// back gracefully when unavailable (each tab tracks its own counters, same
// as before this addition).
type RetryStateMessage =
    | {type: 'set'; itemKey: string; state: RetryState}
    | {type: 'delete'; itemKey: string};

const retryChannel: BroadcastChannel | null =
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('outlaw:retry-state') : null;

if (retryChannel) {
    retryChannel.onmessage = (e: MessageEvent<RetryStateMessage>) => {
        if (!e.data) return;
        if (e.data.type === 'set') {
            recoverableRetryState.set(e.data.itemKey, e.data.state);
        } else if (e.data.type === 'delete') {
            recoverableRetryState.delete(e.data.itemKey);
        }
    };
}

function setRetryState(itemKey: string, state: RetryState): void {
    recoverableRetryState.set(itemKey, state);
    retryChannel?.postMessage({type: 'set', itemKey, state} satisfies RetryStateMessage);
}

function deleteRetryState(itemKey: string): void {
    recoverableRetryState.delete(itemKey);
    retryChannel?.postMessage({type: 'delete', itemKey} satisfies RetryStateMessage);
}

/**
 * Called directly from redux sagas (after data has been manipulated).
 * Also called from fetchFromServer, right before the fetch.
 *
 * Check IndexedDB for anything in the queues, and if found, flush it.
 *
 * Queue names are based on the object: ${name}Queue.
 * Note that the current value for objects is in store.getState().${entityPlural}.
 *
 * Use Redux actions to let the UI know what is going on.
 *
 * @returns {Promise<boolean>}
 */
export async function processQueue(): Promise<boolean> {
    // Compute queueStalled in EVERY tab on every poll, regardless of leadership.
    // This is a read-only IDB inspection, cheap, and avoids the cross-tab
    // visibility gap where a follower would never observe its own queue.
    // Each tab's Redux state is independent; tabs converge to the same
    // queueStalled value within one poll cycle since IDB is shared.
    await checkQueueStalled().catch(err => console.warn('⮃ processQueue: checkQueueStalled error', err));

    // Cross-tab coordination: only one tab at a time may run processQueue.
    // Without this, two tabs polling on slightly-offset intervals can each
    // read the same fatalError'd item from IDB, both clear+retry it, and
    // race two saves of the same payload to the server.
    //
    // ifAvailable=true means: try to acquire; if another tab holds the lock,
    // resolve immediately without waiting. The non-leader tab returns false
    // and tries again on the next 5s poll.
    //
    // Web Locks API support: Chrome 69+, Firefox 96+, Safari 15.4+. The
    // typeof guard preserves processing for any environment (e.g. test
    // shims) where the API isn't available.
    if (typeof navigator !== 'undefined' && navigator.locks) {
        return navigator.locks.request(
            'outlaw:processQueue',
            {ifAvailable: true},
            async (lock) => {
                if (!lock) {
                    if (logging) console.debug('⮃ processQueue: another tab is processing — skipping this poll');
                    return false;
                }
                return _runProcessQueue();
            },
        );
    }
    return _runProcessQueue();
}

/**
 * Scan IDB for items that have been pending longer than QUEUE_STALL_THRESHOLD_MS
 * and aren't in active healthy backoff. Dispatch the queueStalled flag so the
 * StatusBar can render the maroon indicator.
 *
 * Items at MAX_FATAL_ATTEMPTS (truly STUCK) qualify as stalled.
 * Items in active healthy backoff (nextRetryAt > now AND attempts < MAX) do
 * NOT qualify — they're being retried, not stuck.
 *
 * Note `addedOn` is microseconds since epoch (per timestamp.ts); divide by
 * 1000 to compare against Date.now() in ms.
 */
async function checkQueueStalled(): Promise<void> {
    const cutoffMs = Date.now() - QUEUE_STALL_THRESHOLD_MS;
    let stalled = false;
    for (const entity of ENTITIES) {
        const result = await idb.getSingleStore(entity.name);
        if (result.isErr()) continue;
        for (const item of result.unwrap()) {
            if (!item.addedOn) continue;
            const addedMs = item.addedOn / 1000;  // HRT (microseconds) → ms
            if (addedMs >= cutoffMs) continue;    // not old enough

            // Skip items in active healthy backoff — they're being retried.
            const itemKey = `${entity.name}:${item.id}:${item.addedOn}`;
            const state = recoverableRetryState.get(itemKey);
            if (state && state.nextRetryAt > Date.now() && state.attempts < MAX_FATAL_ATTEMPTS) continue;

            stalled = true;
            break;
        }
        if (stalled) break;
    }

    const previous = store.getState().statusBar.queueStalled;
    if (previous !== stalled) {
        console.warn(`⮃ processQueue: queueStalled transitioned to ${stalled}`);
        store.dispatch(StatusBarActions.updateQueueStalled(stalled));
    }
}

async function _runProcessQueue(): Promise<boolean> {
    // Prevent concurrent executions WITHIN A TAB — two overlapping calls
    // can read the same IDB items and send duplicate POSTs to the server.
    // (Cross-tab races are handled by the Web Locks gate in processQueue.)
    if (_processQueueRunning) {
        if (logging) console.debug('⮃ processQueue: already running, skipping');
        return false;
    }

    // If we aren't connected to the server, no point processing.
    // The _testBypassReadinessGates escape hatch is used by Playwright
    // queue specs that exercise lock/retry/healing logic without
    // depending on a live socket.io connection in the test env.
    if (!_testBypassReadinessGates) {
        if (!store.getState().misc.isConnected || !isConnected()) return false;

        // Assertions
        if (!Auth.isAuthenticated()) {
            if (logging) console.debug('⮃ processQueue: processQueue called when not authorized');
            return false;
        }

        if (!(socket && !!socket.connected)) {
            if (logging) console.debug('⮃ processQueue: processQueue called when not connected');
            return false;
        }
    }

    // We do not allow mixing uploads and downloads, as it leads to items "disappearing" until the next download.
    // Instead, we wait for uploading/fetching to stop before moving forward.
    await waitForProcessingToFinish(1_500, 100);

    _processQueueRunning = true;

    // Mark uploading status to true (this is the mutex that the previous pWaitFor code is checking for)
    if (logging) console.debug('⮃ processQueue: processQueue looking for updates');

    try {
        // Get all queued items and process them
        const toBeProcessed = [];
        for (let i = 0; i < ENTITIES.length; i++) {
            const entity = ENTITIES[i];

            try {
                // Wait for restore to finish
                if (logging) if (store.getState().misc.restoreStatus !== RestoreStatus.Restored)
                    console.debug(`⮃ processQueue: Waiting 5 seconds for restore to finish before processing the queue.`);

                try {
                    await pWaitFor(() => {
                        return store.getState().misc.restoreStatus === RestoreStatus.Restored
                    }, {timeout: 5_000, interval: 100, signal: globalAbortSignal});
                }
                catch (err: any) {
                    if (logging) console.debug(`%c⮃ processQueue: Timed out waiting 5 seconds for the restore to finish.`, {color: 'orange'});
                }

                const entityQueueResult = await idb.getSingleStore(entity.name);
                if (entityQueueResult.isErr()) continue;
                const entityQueue = entityQueueResult.unwrap();

                if (entityQueue.length > 0) {
                    if (logging) console.debug(`⮃ processQueue: ${entity.plural}`, entityQueue.map(item => item[`_${entity.name}Id`]).join(','));
                }

                // Decide which items are eligible for processing this poll.
                const nonFatalItems = entityQueue.filter(item => {
                    // Turn into a real object
                    ensure(entity.clazz, item);

                    // No error — process normally.
                    if (!item.fatalError?.message) return true;

                    // Per-message healing first: if we have a known fix for the
                    // specific validation error, mutate the item, clear the
                    // error, and retry as a fresh attempt.
                    // @ts-ignore
                    if (item.fatalError.message === 'fileName is required') {
                        if (!(item as Document).fileName) {
                            console.warn(`⮃ processQueue: ${entity.name} ${item.id} fatalError says missing fileName, adding one`, item);
                            (item as Document).fileName = 'doc';
                        }
                        else {
                            console.warn(`⮃ processQueue: ${entity.name} ${item.id} fatalError says missing fileName, but it has one, adding`, item);
                            item.fatalError = null;
                            return true;
                        }
                    }
                    else if (item.fatalError.message === 'title is required') {
                        console.warn(`⮃ processQueue: ${entity.name} ${item.id} retrying after '${item.fatalError.message}' — defaulting title`, item);
                        (item as Task).title = 'Please add title';
                        item.fatalError = null;
                        return true;
                    }
                    else if (item.fatalError.message === 'urgency is required') {
                        console.warn(`⮃ processQueue: ${entity.name} ${item.id} no urgency specified, retrying with Medium`, item);
                        (item as Task).urgency = 'M';
                        item.fatalError = null;
                        return true;
                    }
                    else if (item.fatalError.message === 'impact is required') {
                        console.warn(`⮃ processQueue: ${entity.name} ${item.id} no impact specified, retrying with Medium`, item);
                        (item as Task).impact = 'M';
                        item.fatalError = null;
                        return true;
                    }

                    // OWA-491: a 409 Conflict is a unique-violation (two
                    // milestones at the same position, a duplicate red-flag
                    // type, an email already in use, ...). Re-sending the
                    // identical payload can NEVER succeed — the user must change
                    // the conflicting value. So do NOT retry it (that's the
                    // ~40-min doomed-retry loop we're killing). Leave fatalError
                    // intact so the item stays queued, the entity renders in its
                    // error state, and the Home dashboard "Needs Attention" card
                    // surfaces it with the friendly message + a link to fix.
                    if (item.fatalError.status === 409) {
                        if (logging) console.debug(`⮃ processQueue: ${entity.name} ${item.id} 409 conflict — not retrying, awaiting user fix`, item);
                        return false;
                    }

                    // Anything else with a fatalError: treat as transient and
                    // bounded-retry. Empirically, the vast majority of "stuck"
                    // items succeed on retry (auth-token expiry, gateway
                    // timeouts, brief network blips, rate limiting, concurrent-
                    // update conflicts, etc.) — clearing fatalError and trying
                    // again is what the manual Retry Cache button does. The
                    // attempt counter caps wasted requests for items that are
                    // genuinely broken (bad payload, permanent permission
                    // denial). After MAX_FATAL_ATTEMPTS the item is left in
                    // place with its fatalError intact, lands in STUCK, and
                    // shows up in the Cache Management UI for human review.
                    const itemKey = `${entity.name}:${item.id}:${item.addedOn}`;
                    const state = recoverableRetryState.get(itemKey) ?? {attempts: 0, nextRetryAt: 0};
                    const now = Date.now();
                    if (state.nextRetryAt > now) {
                        // Backoff window — skip this poll, item stays queued.
                        return false;
                    }
                    if (state.attempts >= MAX_FATAL_ATTEMPTS) {
                        // Exhausted — leave fatalError intact so the STUCK log
                        // below fires and the Cache Management UI shows it.
                        return false;
                    }
                    const nextAttempts = state.attempts + 1;
                    // Backoff: 5s, 10s, 20s, 40s, 80s, 160s, 320s, 640s, 1280s, capped at 10min.
                    const backoffMs = Math.min(5_000 * (1 << (nextAttempts - 1)), 10 * 60 * 1000);
                    // Broadcast so other tabs see the bumped counter and
                    // backoff window — without this, leadership flapping
                    // between tabs would let the same item burn N×tabCount
                    // retries before exhausting MAX_FATAL_ATTEMPTS.
                    setRetryState(itemKey, {attempts: nextAttempts, nextRetryAt: now + backoffMs});
                    if (nextAttempts === 1 || nextAttempts % 3 === 0) {
                        // Throttled logging so a sticky failure doesn't flood the console.
                        console.warn(`⮃ processQueue: ${entity.name} ${item.id} fatalError ${item.fatalError.status}: ${item.fatalError.message} — retry ${nextAttempts}/${MAX_FATAL_ATTEMPTS} (next backoff ${backoffMs}ms)`, item);
                    }
                    item.fatalError = null;
                    return true;
                });

                // Distinguish "STUCK" (exhausted retries — needs human review)
                // from "in backoff" (will retry on a future poll).
                const stuckItems = entityQueue.filter(item => {
                    if (!item.fatalError?.message) return false;
                    const state = recoverableRetryState.get(`${entity.name}:${item.id}:${item.addedOn}`);
                    return !!state && state.attempts >= MAX_FATAL_ATTEMPTS;
                });
                if (stuckItems.length > 0) {
                    console.warn(`⮃ processQueue: not processing ${stuckItems.length} STUCK ${entity.plural}`, stuckItems);
                    stuckItems.forEach(item => {
                        console.warn(`⮃ processQueue: STUCK ${entity.name} ${item.id} — status=${item.fatalError.status} recoverable=${item.fatalError.recoverable} msg="${item.fatalError.message}"`);
                    });
                }

                nonFatalItems.forEach(item => {
                    toBeProcessed.push({entity, item: ensure(entity.clazz, item)});
                });
            }
            catch (err) {
                console.warn(`⮃ processQueue: error processing ${entity.name}: ${err.message}`, err);
            }
        }

        // console.log('toBeProcessed', toBeProcessed);
        if (toBeProcessed.length > 0) {
            store.dispatch(StatusBarActions.updateUploading(true));

            if (logging) console.debug(`⮃ processQueue: processing ${toBeProcessed.length} updates`);

            try {
                await processQueueItems(toBeProcessed);
            }
            catch (err) {
                console.error(`⮃ processQueue: calling processQueueItems: ${err.message}`, err)
            }
            finally {
                store.dispatch(StatusBarActions.updateUploading(false));
            }

            if (logging) console.debug('⮃ processQueue: processQueue finished');
        }

        return true;
    }
    finally {
        _processQueueRunning = false;
        // Self-heal queue counter drift from missed decrements, multi-tab races, partial failures.
        idb.initQueueCount().catch(err =>
            console.warn('⮃ processQueue: initQueueCount failed', err)
        );
    }
}

/**
 * Fetch full or partial results from the server.
 * - Full results are pulled if outlaw.polling.lastFetch is 0.
 * - Otherwise partial is pulled.
 *
 * @param {ProcessQueueOptions} options
 * @param {ProcessQueueMethod} options.type
 * @returns {Promise<boolean>}
 */
export async function fetchFromServer(options: ProcessQueueOptions): Promise<boolean> {
    if (logging) console.debug('⮃ processQueue: fetchFromServer', options);

    // Assertions
    if (!Auth.isAuthenticated()) {
        if (logging) console.debug('%c⮃ processQueue: fetchFromServer called when not authorized', 'color: red');
        return false;
    }
    if (!(socket && socket.connected)) {
        if (logging) console.debug('%c⮃ processQueue: processQueue called when not connected', 'color: orange');
        return false;
    }

    // We do not allow mixing uploads and downloads, as it leads to items "disappearing" until the next download
    if (logging) console.debug(`⮃ processQueue: Waiting 5 seconds for processing to finish before processing the queue.`);
    await waitForProcessingToFinish(5_000, 100);

    // Always flush out the cache first!
    await processQueue();

    // Now do a fetch, if required
    try {
        // Set the fetching flag to true
        store.dispatch(StatusBarActions.updateFetching(true));

        if (logging) console.debug('⮃ processQueue: fetchFromServer running...');

        // Get the restoreStatus.  We don't want to run until we have been fully restored!  Could get messy.
        if (logging) console.debug('⮃ processQueue: Waiting for restore to complete');

        try {
            if (logging) console.debug(`⮃ processQueue: Waiting 10 seconds for restore to finish before processing the queue.`);
            await pWaitFor(
                () => {
                    return store.getState().misc.restoreStatus === MiscActions.RESTORED;
                },
                {timeout: 10_000, interval: 100, signal: globalAbortSignal}
            );
        }
        catch (err) {
            // Just move along.  Something clearly went wrong with the restore.
            if (logging) console.debug('%c⮃ processQueue: fetchFromServer waited 10 seconds for restore to complete, just going.', {color: 'orange'});
        }

        // Full refresh: filter out deleted rows — we're replacing state wholesale and don't need
        // tombstones for non-retained entities (the server still includes D rows for entities in
        // getDeletedForEntities, like contacts and users, so Show-Deleted UIs still work).
        // Partial fetch: must receive D rows so offline-then-online clients can sync deletions that
        // happened while they were away. Reducers splice D rows out of state (or retain them as
        // tombstones per the generator).
        const _options = options.type === 'full' ? {status: {$ne: 'D'}} : {};

        const label = options.type === 'partial' ? 'Checking for updates' : 'Loading your data';
        store.dispatch(MiscActions.setLoadingMessage(label));
        if (logging) console.debug(`⮃ processQueue: fetching ${options.type} data`);

        const fetchFn: (
            type: 'full' | 'partial', e: typeof ENTITIES, opts: any, sort: any, signal?: AbortSignal,
        ) => Promise<unknown> = isPortalUser() ? findMany : findManyParallel;
        await withTimeout(`fetchFromServer(${options.type})`, signal => fetchFn(options.type, ENTITIES, _options, null, signal));

        // In case there is one from when firmId was not yet avaialble
        locals.setString(locals.REFRESH_TYPE, 'partial', false);

        // And now set the firm-specific one
        locals.setString(locals.REFRESH_TYPE, 'partial', true);

        processRunningTasks();

        // Ensure generic category entries exist for case types with subcategories
        CaseHelper.ensureGenericCategoryEntries(store.getState().caseTypes);

        if (logging) console.debug('⮃ processQueue: fetchFromServer finished');
    }
    catch (err) {
        console.error('⮃', err);
    }
    finally {
        // Remove fetching from statusbar
        store.dispatch(MiscActions.setLoadingMessage(''));

        // Set the fetching flag to false
        store.dispatch(StatusBarActions.updateFetching(false));

        // Reconcile counter even if processQueue early-bailed (lock held, not connected, etc.).
        idb.initQueueCount().catch(err =>
            console.warn('⮃ processQueue: initQueueCount failed in fetchFromServer', err)
        );
    }
}

/**
 * Do a check to make sure we don't have more than one timer running per person.
 * If we do, turn off all but the most recent one.
 *
 * Also reconciles LONE zombie timers: a single timer that has been running
 * longer than MAX_AGE (24h) is stale (e.g. "timer still running the next day",
 * OWA-7) and is stopped here even when it is the user's only running timer.
 *
 * Exported only so it can be unit-tested; behavior is unchanged when called
 * from fetchFromServer.
 */
export function processRunningTasks() {
    const tasks = store.getState().tasks?.getIndex('notCompleted')?.filter(t => t.running && t.status !== 'D') ?? [];

    const MAX_AGE = 24 * 60 * 60 * 1000;

    // Divide the tasks by user
    const userIds = tasks.map(t => t.assignedTo).filter(distinctFilter);
    userIds.forEach(uid => {
        // Get all tasks for this user, sorted by addedOn descending
        const userTasks = tasks.filter(t => t.assignedTo === uid).sort((a, b) => b.addedOn - a.addedOn);
        if (userTasks.length > 1) {
            console.warn(`processRunningTasks: user ${uid} has ${userTasks.length} running tasks. Stopping ${userTasks.length - 1} older ones.`,
                userTasks.map(t => ({id: t.id, addedOn: t.addedOn, lastStart: t.lastStart, elapsed: t.elapsedTime})));
            // Stop each of the older timers.
            userTasks.slice(1).forEach(t => {
                if (t.lastStart && (Date.now() - t.lastStart > MAX_AGE)) {
                    console.warn(`processRunningTasks: zombie timer detected. Task ${t.id}, lastStart=${new Date(t.lastStart).toISOString()}`);
                }
                stopTimer(t, uid);
            });
        }
        else if (userTasks.length === 1) {
            // Lone running timer: only stop it if it is a stale zombie (running
            // longer than MAX_AGE). Recent single timers are left untouched.
            const [t] = userTasks;
            if (t.lastStart && (Date.now() - t.lastStart > MAX_AGE)) {
                console.warn(`processRunningTasks: lone zombie timer detected. Task ${t.id}, lastStart=${new Date(t.lastStart).toISOString()}. Stopping.`);
                stopTimer(t, uid);
            }
        }
    })
}

/**
 * Take a list of sorted items, and create an array of arrays of items, grouped by type and status (U/C vs D), as they occur.
 *
 * e.g. input: [task1, task2, task3, task4(D), task5, option1, option2, option3(D), option4(D), option5
 *     output: [[task1, task2, task3], [task4], [task5], [option1, option2], [option3, option4], [option5]
 * @param {Array.<{entity: object, item: object}>} items
 * @returns {Array.<Array.<{entity: object, item: object}>>}
 */
function group(items) {
    if (logging) console.debug('⮃ processQueue: group', items);

    // Handle the null cases
    if (!items || items.length === 0) return [];

    // Prime the results with a set for the first entry
    let pair = items[0];
    const results = [[pair]];

    // Keep track of what where we are from an entityName/status perspective
    let currentGroup = 0;
    let currentEntity = pair.entity.name;
    let currentStatus = pair.item.status === 'D' ? 'D' : 'S';

    for (let i = 1; i < items.length; i++) {
        // If this is the same
        const {entity, item} = items[i];
        const status = item.status === 'D' ? 'D' : 'S';
        if (entity.name === currentEntity && status === currentStatus) results[currentGroup].push(items[i]);
        else {
            results[++currentGroup] = [items[i]];
            currentEntity = entity.name;
            currentStatus = status;
        }
    }

    if (logging) console.debug('⮃ processQueue: group result', results);
    return results;
}

/**
 * Process items in the queue (in strict order)
 *
 * @param {Array.<{entity: object, item: object}>} items
 */
async function processQueueItems(items) {
    if (logging) console.debug('⮃ processQueue: processQueueItems', items);

    // Sort the items by date (addedOn) and process IN ORDER
    const sortedItems = items.sort((a, b) => a.item.addedOn - b.item.addedOn);
    const groupedItems = group(sortedItems);

    // We now support saving multiple items as a batch.  To do this, we can't just rattle through the queue one at a time.
    for (let i = 0; i < groupedItems.length; i++) {
        const group = groupedItems[i];
        const entity = group[0].entity; // All the same within the group, so just take the first one
        const items = group.map(pair => pair.item); // Just the embedded items

        try {
            if (items[0].status !== 'D') {
                if (items.length > 1) {
                    // Batch save
                    if (logging) console.debug(`⮃ processQueue: Save requested for ${entity.plural}`, items.map(item => item.id));
                    const toSave = items.filter(i => !i.restore);
                    const toRestore = items.filter(i => i.restore);

                    if (toSave.length) {
                        if (logging) console.debug(`⮃ processQueue: before ${toSave.length > 1 ? 'save' : 'saveMany'} (${entity})`);

                        // Try to save them all at once first
                        if (toSave.length > 1) {
                            let failed = false;

                            try {
                                await withTimeout(`saveMany(${entity.plural})`, signal => saveMany(entity, items, signal));
                            }
                            catch (err) {
                                console.error(`⮃ processQueue: Error saving ${entity.name}: ${err.message}`, err);
                                failed = true;
                            }

                            if (failed) {
                                // Process one at a time
                                for (let i = 0; i < items.length; i++) {
                                    try {
                                        await withTimeout(`save(${entity.name} ${items[i].id})`, signal => save(entity, items[i], signal));
                                    }
                                    catch (err) {
                                        console.error(`⮃ processQueue: Error saving ${entity.name}: ${err.message}`, err);
                                    }
                                }
                            }
                        }
                        else {
                            try {
                                // TODO: This needs to handle the result, not use try/catch
                                await withTimeout(`saveMany(${entity.plural})`, signal => saveMany(entity, items, signal));
                            }
                            catch (err) {
                                console.error(`⮃ processQueue: Error saving ${entity.name}: ${err.message}`, err);

                                // Try individually
                                for (let item of items) {
                                    await withTimeout(`save(${entity.name} ${item.id})`, signal => save(entity, item, signal));
                                }
                            }
                        }

                        if (logging) console.debug(`⮃ processQueue: after ${toSave.length > 1 ? 'save' : 'saveMany'} (${entity})`);
                    }

                    if (toRestore.length) {
                        for (let i = 0; i < toRestore.length; i++) {
                            if (logging) console.debug('⮃ processQueue: before restore');

                            // If we can't restore, the specific error will be thrown within the restore call
                            try {
                                await withTimeout(`restore(${entity.name} ${toRestore[i].id})`, signal => restore(entity, toRestore[i], signal));
                            }
                            catch (err) {
                                console.error(`⮃ processQueue: Error restoring ${entity.name}: ${err.message}`, err);
                            }

                            if (logging) console.debug('⮃ processQueue: after restore');
                        }
                    }
                }
                else {
                    // Process a single entity, either a save or a restore
                    const item = items[0];
                    if (item.restore) {
                        if (logging) console.debug(`⮃ processQueue: before restore`);
                        await withTimeout(`restore(${entity.name} ${item.id})`, signal => restore(entity, item, signal));
                        if (logging) console.debug(`⮃ processQueue: after restore`);
                    }
                    else {
                        if (logging) console.debug(`⮃ processQueue: before save`);
                        await withTimeout(`save(${entity.name} ${item.id})`, signal => save(entity, item, signal));
                        if (logging) console.debug(`⮃ processQueue: after save`);
                    }
                }
            }
            else {
                for (let item of items) {
                    if (logging) console.debug(`⮃ processQueue: Delete requested for ${entity.name} ${item.id}`);
                    await withTimeout(`remove(${entity.name} ${item.id})`, signal => remove(entity, item, signal));
                }
            }
        }
        catch (err) {
            console.error(`⮃ processQueue: error processing ${entity.name}: ${err.message}`);
        }
    }
}

/**
 * Test-only handles into the retry-state machinery. Imported by
 * `lib/testHooks.ts` and re-exposed on `window.__test_outlaw.retry` for
 * Playwright specs that need to drive bounded-retry behavior (e.g.
 * forcing `nextRetryAt` to 0 between iterations to compress a 10-retry
 * burn-down from ~40 minutes of real backoff to a few seconds).
 *
 * Production builds tree-shake the importer (`testHooks.ts` is no-op
 * when `import.meta.env.PROD`), so this surface area never lands in
 * the shipped bundle.
 */
export const __test_retry = {
    /** Read the current retry state for an item (undefined if no failures yet). */
    getState: (itemKey: string): RetryState | undefined => recoverableRetryState.get(itemKey),
    /** Reset only `nextRetryAt` to 0; preserve `attempts` so the bounded-retry counter advances. */
    resetNextRetryAt: (itemKey: string): void => {
        const state = recoverableRetryState.get(itemKey);
        if (state) {
            setRetryState(itemKey, {attempts: state.attempts, nextRetryAt: 0});
        }
    },
    /** Clear all retry state. Useful as a per-test reset. */
    clearAll: (): void => {
        recoverableRetryState.clear();
    },
    /** Compose the retry-state key the queue uses internally. */
    keyFor: (entityName: string, id: string, addedOn: number | string): string =>
        `${entityName}:${id}:${addedOn}`,
    /** The bounded-retry cap; mirrors MAX_FATAL_ATTEMPTS. */
    maxFatalAttempts: MAX_FATAL_ATTEMPTS,
    /**
     * Bypass the isConnected/Auth/socket.connected readiness gates in
     * `_runProcessQueue()` for the duration of a test. The gates are
     * tested by the unit-mode `processQueue.test.ts`; Playwright queue
     * specs care about lock/retry/healing logic instead.
     */
    setBypassReadinessGates: (bypass: boolean): void => {
        _testBypassReadinessGates = bypass;
    },
};
